import { useState, useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const useSearchHandler = <T>(initialQuery = '') => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState<string>(initialQuery);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [hasSearched, setHasSearched] = useState<boolean>(false);
  const [autoSearchTriggered, setAutoSearchTriggered] = useState<boolean>(false);
  const [isAutoSearchLoading, setIsAutoSearchLoading] = useState<boolean>(
    () => new URLSearchParams(location.search).has('autoSearch'),
  );

  // Auto-fill search from URL parameter (from smart redirect)
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const autoSearch = searchParams.get('autoSearch');

    if (autoSearch && autoSearch !== searchQuery && !autoSearchTriggered) {
      console.log('🔍 [SEARCH HANDLER] Auto-search triggered with:', autoSearch);
      setSearchQuery(autoSearch);
      setHasSearched(true);
      setAutoSearchTriggered(true);
      setIsAutoSearchLoading(true);
    }
  }, [location.search, searchQuery, autoSearchTriggered]);

  const handleSearch = useCallback(async (query: string, refetch: () => Promise<T>) => {
    setSearchQuery(query);
    setIsSearching(true);
    setHasSearched(true);
    await refetch();
    setIsSearching(false);
  }, []);

  const resetSearch = useCallback(async (refetch: () => Promise<T>) => {
    setSearchQuery('');
    setIsSearching(false);
    setHasSearched(false);
    setIsAutoSearchLoading(false);

    // Nettoyer les paramètres autoSearch/openExternalComments de l'URL via React Router
    const params = new URLSearchParams(location.search);
    if (params.has('autoSearch') || params.has('openExternalComments')) {
      params.delete('autoSearch');
      params.delete('openExternalComments');
      const newSearch = params.toString();
      navigate({ pathname: location.pathname, search: newSearch ? `?${newSearch}` : '' }, { replace: true });
    }

    await refetch();
  }, [location, navigate]);

  return {
    searchQuery,
    isSearching,
    hasSearched,
    isAutoSearchLoading,
    setIsAutoSearchLoading,
    handleSearch,
    resetSearch,
  };
};

export default useSearchHandler;
